import Cookies from 'js-cookie';
import aa from 'search-insights';

import { getProductPageUrl, getVideoEmbedSync } from '../../../utils';
import { APP_ID, SEARCH_KEY } from './algolia-api';

//	Reads the `?q=` search term from a Gatsby `location` object. Shared so the
//	header flyout (DefaultLayout) and the /search results page parse it the
//	same way, rather than each having their own copy.
export const getQueryParam = _location => new URLSearchParams(_location?.search || '').get('q') || '';

//	Optional `tab` param (products | content | videos): the results page opens on this
//	tab, e.g. when a flyout search suggestion from that category was clicked.
export const SEARCH_TABS = ['products', 'content', 'videos'];
export const getTabParam = _location => {
	const tab = new URLSearchParams(_location?.search || '').get('tab');
	return SEARCH_TABS.includes(tab) ? tab : '';
};

//	------------------------------------------------------------------
//	Initializes the Algolia Insights client once, globally, on every page --
//	not only when InstantSearch happens to mount on the /search results
//	page. react-instantsearch's `insights` prop would otherwise call
//	`aa('init', ...)` for us, but only on pages where it renders, which
//	leaves the header search flyout (a plain Algolia query, no
//	InstantSearch) without an initialized client or a persisted
//	userToken. Calling `aa('init', ...)` here guarantees a stable,
//	cookie-backed userToken exists from first page load, regardless of
//	where the shopper actually clicks a search result. Safe to call more
//	than once (e.g. if InstantSearch also initializes it later) -- it
//	just re-applies the same credentials.
export const initAlgoliaInsights = () => {
	if (typeof window === 'undefined') return;
	if (!APP_ID || !SEARCH_KEY) return;

	aa('init', {
		appId: APP_ID,
		apiKey: SEARCH_KEY,
		useCookie: true,
	});
};

//	------------------------------------------------------------------
//	Send Insights events only when Shopify Customer Privacy allows analytics.
//	Shared by the search UI (SearchAlgolia.js) and the add-to-cart flow
//	(checkout-hook.js), so both sides honor the same consent check.
//	------------------------------------------------------------------
export const analyticsAllowed = () => {
	if (typeof window === 'undefined') return false;
	const cp = window.Shopify && window.Shopify.customerPrivacy;
	if (cp) {
		if (typeof cp.analyticsProcessingAllowed === 'function') return !!cp.analyticsProcessingAllowed();
		if (typeof cp.userCanBeTracked === 'function') return !!cp.userCanBeTracked();
	}
	return true;
};

//	------------------------------------------------------------------
//	Bridges a search-result click to the (possibly much later, possibly
//	different-page) add-to-cart action, so we can fire an attributed
//	Algolia conversion ("Product Added To Cart") only when the add is
//	really related to a search.
//
//	`objectID` on a hit is a Shopify *variant* id (the index shows only
//	one record per product, to keep results from ballooning with every
//	variant, but that one record's objectID is still a variant id --
//	typically the product's default variant). We never re-derive or
//	reinterpret it: whatever objectID Algolia showed the shopper at
//	click time is what we replay verbatim in the conversion, since
//	that's the value Algolia needs back to attribute it to that search.
//
//	Matching happens on the Shopify *product* id instead (`hit.id`,
//	distinct from `objectID`), because that's what's stable regardless
//	of which variant the shopper actually ends up adding to cart.
//	------------------------------------------------------------------
const SEARCH_CLICK_STORAGE_KEY = 'algolia_search_click';

//	sessionStorage already dies with the tab, which is the real "price-shop
//	around and come back later" boundary -- this is just a safety-net
//	ceiling in case a browser restores sessionStorage across a restart,
//	so an add-to-cart doesn't get attributed to a search from a different
//	browsing session days earlier.
const SEARCH_CLICK_MAX_AGE_MS = 24 * 60 * 60 * 1000; // 24 hours

//	One entry per product, not one overwritten slot -- a shopper who clicks
//	two different search results (then adds both to cart) needs both
//	remembered, not just the most recent click. Capped so a very long
//	browsing session can't grow this unboundedly.
const SEARCH_CLICK_MAX_ENTRIES = 50;

const readSearchClickStore = () => {
	try {
		const raw = window.sessionStorage.getItem(SEARCH_CLICK_STORAGE_KEY);
		return raw ? JSON.parse(raw) : {};
	} catch (e) {
		return {};
	}
};

export const storeSearchClickContext = ({
	objectID, productId, queryID, index,
}) => {
	if (typeof window === 'undefined' || !window.sessionStorage) return;
	if (!analyticsAllowed()) return;
	if (!objectID || !productId || !queryID || !index) return;

	try {
		const store = readSearchClickStore();
		store[String(productId)] = {
			objectID, queryID, index, storedAt: Date.now(),
		};

		//	Prune oldest entries first if we're over the cap.
		const entries = Object.entries(store);
		if (entries.length > SEARCH_CLICK_MAX_ENTRIES) {
			entries
				.sort(([, a], [, b]) => a.storedAt - b.storedAt)
				.slice(0, entries.length - SEARCH_CLICK_MAX_ENTRIES)
				.forEach(([key]) => delete store[key]);
		}

		window.sessionStorage.setItem(SEARCH_CLICK_STORAGE_KEY, JSON.stringify(store));
	} catch (e) {
		// sessionStorage unavailable (private mode, quota, etc.) -- safe to no-op.
	}
};

//	Returns { objectID, queryID, index } only when there's a stored click for
//	this specific product, it's still fresh, and analytics is allowed.
//	Otherwise returns null -- the caller should fire nothing.
export const getSearchClickContext = productId => {
	if (typeof window === 'undefined' || !window.sessionStorage) return null;
	if (!analyticsAllowed()) return null;
	if (!productId) return null;

	const store = readSearchClickStore();
	const stored = store[String(productId)];

	if (!stored?.objectID || !stored?.queryID || !stored?.index || !stored?.storedAt) return null;
	if (Date.now() - stored.storedAt > SEARCH_CLICK_MAX_AGE_MS) return null;

	return { objectID: stored.objectID, queryID: stored.queryID, index: stored.index };
};

//	Hidden cart-line properties read by the Shopify checkout custom pixel (which runs on
//	the checkout subdomain and can't see our storage or cookies) to report the purchase
//	to Algolia, attributed to the search that led to it. Returns [] unless this product
//	has a fresh, consented search click. `_algoliaUserToken` is the storefront's Insights
//	token (its cookie), so purchase and search events share one user.
export const ALGOLIA_CART_ATTRIBUTE_PREFIX = '_algolia';

export const getAlgoliaCartAttributes = productId => {
	const context = getSearchClickContext(productId);
	if (!context) return [];

	const userToken = Cookies.get('_ALGOLIA');

	return [
		{ key: '_algoliaQueryID', value: context.queryID },
		{ key: '_algoliaObjectID', value: String(context.objectID) },
		{ key: '_algoliaIndex', value: context.index },
		...(userToken ? [{ key: '_algoliaUserToken', value: userToken }] : []),
	];
};

//	Fires the Algolia Insights "clicked" event for places that don't render
//	through react-instantsearch's useInfiniteHits/sendEvent helper (the
//	header search flyout uses a raw client.search() call), so the event
//	has to be built by hand instead.
export const sendProductClickEvent = ({
	objectID, queryID, index, position,
}) => {
	if (!analyticsAllowed()) return;
	if (!objectID || !queryID || !index || !position) return;

	aa('clickedObjectIDsAfterSearch', {
		eventName: 'Product Clicked',
		index,
		queryID,
		objectIDs: [objectID],
		positions: [position],
	});
};

//	------------------------------------------------------------------
//	Review rating. Records store `meta.reviews.rating` as a JSON *string*,
//	e.g. '{"scale_min":"1.0","scale_max":"5.0","value":"4.9"}'. Parse it
//	to a number on a 5-star scale (or null when absent / unparseable).
//	------------------------------------------------------------------
export const parseRatingValue = raw => {
	if (raw == null) return null;

	let value = null;
	let scaleMax = 5;

	const read = obj => {
		value = parseFloat(obj.value);
		const max = parseFloat(obj.scale_max);
		if (Number.isFinite(max) && max > 0) scaleMax = max;
	};

	if (typeof raw === 'number') {
		value = raw;
	} else if (typeof raw === 'object') {
		read(raw);
	} else if (typeof raw === 'string') {
		try {
			read(JSON.parse(raw));
		} catch (e) {
			//	Fallback for oddly-escaped strings: pull the numbers out directly.
			const valueMatch = raw.match(/value\D*?([0-9.]+)/i);
			const scaleMatch = raw.match(/scale_max\D*?([0-9.]+)/i);
			if (valueMatch) value = parseFloat(valueMatch[1]);
			if (scaleMatch) {
				const max = parseFloat(scaleMatch[1]);
				if (Number.isFinite(max) && max > 0) scaleMax = max;
			}
		}
	}

	if (!Number.isFinite(value) || value <= 0) return null;

	const stars = scaleMax === 5 ? value : (value / scaleMax) * 5;
	return Math.round(Math.min(5, Math.max(0, stars)) * 10) / 10;
};

//	------------------------------------------------------------------
//	Map one Algolia product hit to the shape the results card renders.
//	Algolia's data is already clean, so the field names stay natural.
//	------------------------------------------------------------------
export const mapProductHit = hit => {
	const {
		objectID,
		title,
		price,
		image,
		product_image: productImage,
		handle,
		product_type: productType,
		meta,
		body_html_safe: bodyHtml,
	} = hit;

	// const rating = parseRatingValue(meta?.reviews?.rating);

	return {
		objectID,
		title,
		price,
		image: image || productImage || '',
		productType,
		url: getProductPageUrl({ productType, handle }) || `/products/${ handle }`,
		description: meta?.custom_fields?.short_description || bodyHtml || '',
		// review: rating != null ? { value: rating } : null,
	};
};

export const mapProductHits = (hits = []) => hits.map(mapProductHit);

//	------------------------------------------------------------------
//	Maps a single hit from the header search flyout's raw suggestProducts()
//	call (not react-instantsearch, so hits don't arrive with __queryID/
//	__position already attached the way they do on the results page).
//	`queryID` comes from the *response*, not the hit, and `index` is the
//	hit's own position within that response's `hits` array -- the same
//	two pieces an Insights "clicked"/"converted" event needs, just made
//	explicit here instead of silently defaulting on the shared mapper.
//	------------------------------------------------------------------
export const mapSuggestHit = (hit, { index, queryID } = {}) => ({
	...mapProductHit(hit),
	id: hit.id,
	queryID,
	position: index + 1,
});

//	------------------------------------------------------------------
//	Sort dropdown options. `value` matches the keys in PRODUCT_SORT_INDICES.
//	------------------------------------------------------------------
export const getProductSortOptions = () => [
	{ label: 'Relevance', value: 'relevance' },
	{ label: 'Name', value: 'title asc' },
	{ label: 'Newest', value: 'date desc' },
	{ label: 'Price: High to Low', value: 'price desc' },
	{ label: 'Price: Low to High', value: 'price asc' },
];

// Map a content/blog hit to the shape ContentCard renders.
const mapContentHit = hit => ({
	objectID: hit.objectID,
	title: hit.title || '',
	description: hit.description || '',
	image: hit.image || '',
	url: hit.url || hit.objectID,
});

export const mapContentHits = (hits = []) => hits.map(mapContentHit);

// Map a video hit to the shape VideoCard renders (`url` = the video's page).
const mapVideoHit = hit => ({
	objectID: hit.objectID,
	title: hit.title || '',
	image: hit.imageUrl || '',
	url: hit.productUrl || hit.videoUrl,
	embed: getVideoEmbedSync(hit.videoUrl, { enablejsapi: true }) || null,
});

export const mapVideoHits = (hits = []) => hits.map(mapVideoHit);

//	Facet config — the single source of truth for which facets exist, their label,
//	and how their data is shaped. `type` drives the shaper (below) and the UI.
export const FACETS = [
	// { attribute: 'category.lvl0', label: 'Category', type: 'list' },
	{ attribute: 'meta.custom_fields.facet_category', label: 'Product Type', type: 'list' },
	{ attribute: 'meta.custom_fields.brand', label: 'Brand', type: 'list' },
	{ attribute: 'meta.custom_fields.color', label: 'Color', type: 'list' },
	// { attribute: 'meta.custom_fields.features', label: 'Features', type: 'list' },
	{ attribute: 'meta.custom_fields.material', label: 'Material', type: 'list' },
	{ attribute: 'meta.reviews.rating', label: 'Rating', type: 'rating' },
	{ attribute: 'price', label: 'Price', type: 'range' },
];

//	One shaper per facet type: a raw disjunctiveFacets entry -> UI-ready values.
const SHAPERS = {
	list: ({ data = {} }) => Object.keys(data).sort((a, b) => data[b] - data[a]),
	//	Rating values are opaque JSON strings; keep them raw (parsed where needed).
	rating: ({ data = {} }) => Object.keys(data),
	range: ({ data = {}, stats }) => {
		const nums = Object.keys(data).map(Number).filter(Number.isFinite);
		let min = Number.isFinite(stats?.min) ? stats.min : null;
		let max = Number.isFinite(stats?.max) ? stats.max : null;
		if (min == null && nums.length) min = Math.min(...nums);
		if (max == null && nums.length) max = Math.max(...nums);
		return (min != null && max != null) ? [min, max] : null;
	},
};

//	Facet snapshot: for each configured facet, find its raw entry in
//	results.disjunctiveFacets and shape it by type. Returns an ordered array of
//	{ attribute, label, type, values } — the single source of truth for the facet UI.
export const mapFacetSnapshot = (disjunctiveFacets = []) => FACETS
	.map(({ attribute, label, type }) => {
		const raw = disjunctiveFacets.find(facet => facet.name === attribute);
		if (!raw) return null;
		const values = SHAPERS[type](raw);
		return values && values.length ? {
			attribute, label, type, values,
		} : null;
	})
	.filter(Boolean);
